import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Breadcrumb,
  Button,
  Card,
  Divider,
  Dropdown,
  Result,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CopyOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  LinkOutlined,
  MoreOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import formatDistance from "date-fns/formatDistance";
import { it } from "date-fns/locale";
import InfiniteScroll from "react-infinite-scroll-component";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";
import config from "../../../api_config.json";
import { useAppContext } from "../../../context";
import SelectOrg from "../../SelectOrg/SelectOrg";
import { getThumbnailUrl } from "../../common/utils/thumbnails";
import {
  AI_RESULT_TYPES,
  getAttachmentPresentation,
  parseAiTypesParam,
  serializeAiTypesParam,
} from "./aiResultTypes";
import useGiottoAiResults from "./useGiottoAiResults";
import "./GiottoAiResults.css";

const { Text, Title } = Typography;
const PREVIEW_WIDTH = 600;

const getScalarParam = (value) => {
  if (Array.isArray(value)) return value[0] || null;
  return value || null;
};

const getOrgDetailUrl = (orgId, typeSlugs) => {
  const types = serializeAiTypesParam(typeSlugs);
  const search = queryString.stringify({ view: "results", types: types || undefined });
  return `/orgs/${orgId}/ai?${search}`;
};

const GiottoAiResultCard = ({
  attachment,
  isProd,
  showOrganization,
  activeTypeSlugs,
}) => {
  const [showExtraLayer, setShowExtraLayer] = useState(false);
  const {
    types,
    beforeUrl,
    afterUrl,
    extraLayerUrl,
    extraLayerLabel,
  } = getAttachmentPresentation(attachment);
  const previews = useMemo(
    () => ({
      before: getThumbnailUrl(beforeUrl, isProd, PREVIEW_WIDTH),
      after: getThumbnailUrl(afterUrl, isProd, PREVIEW_WIDTH),
      extraLayer: getThumbnailUrl(extraLayerUrl, isProd, PREVIEW_WIDTH),
    }),
    [afterUrl, beforeUrl, extraLayerUrl, isProd]
  );

  const handleDownload = useCallback((url, filename) => {
    if (!url) {
      message.warning("URL immagine non disponibile");
      return;
    }

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleCopyLink = useCallback((url, label) => {
    if (!url) {
      message.warning(`Link ${label} non disponibile`);
      return;
    }

    navigator.clipboard.writeText(url).then(() => {
      message.success(`Link ${label} copiato negli appunti`);
    }).catch(() => {
      message.error("Errore durante la copia del link");
    });
  }, []);

  const menuItems = [
    {
      key: "download-original",
      icon: <DownloadOutlined />,
      label: "Scarica originale",
      disabled: !beforeUrl,
      onClick: () => handleDownload(beforeUrl, `originale_${attachment.objectId}.png`),
    },
    {
      key: "download-result",
      icon: <DownloadOutlined />,
      label: "Scarica risultato",
      disabled: !afterUrl,
      onClick: () => handleDownload(afterUrl, `risultato_${attachment.objectId}.png`),
    },
    { type: "divider" },
    {
      key: "copy-original",
      icon: <CopyOutlined />,
      label: "Copia link originale",
      disabled: !beforeUrl,
      onClick: () => handleCopyLink(beforeUrl, "originale"),
    },
    {
      key: "copy-result",
      icon: <CopyOutlined />,
      label: "Copia link risultato",
      disabled: !afterUrl,
      onClick: () => handleCopyLink(afterUrl, "risultato"),
    },
    ...(extraLayerUrl
      ? [
          { type: "divider" },
          {
            key: "download-extra-layer",
            icon: <DownloadOutlined />,
            label: `Scarica ${extraLayerLabel.toLowerCase()}`,
            onClick: () => handleDownload(
              extraLayerUrl,
              `${extraLayerLabel.toLowerCase()}_${attachment.objectId}.png`
            ),
          },
          {
            key: "copy-extra-layer",
            icon: <CopyOutlined />,
            label: `Copia link ${extraLayerLabel.toLowerCase()}`,
            onClick: () => handleCopyLink(
              extraLayerUrl,
              extraLayerLabel.toLowerCase()
            ),
          },
        ]
      : []),
    { type: "divider" },
    {
      key: "parse-dashboard",
      icon: <DatabaseOutlined />,
      label: "Apri risorsa in Parse Dashboard",
      onClick: () => window.open(
        `${config.parseDashboard[isProd ? "prod" : "dev"]}` +
          `/browser/allegati?filters=[{"field":"objectId","constraint":"eq","compareTo":"${attachment.objectId}"}]`,
        "_blank",
        "noopener,noreferrer"
      ),
    },
    ...(showOrganization && attachment.org_pointer?.objectId
      ? [
          {
            key: "org-details",
            icon: <LinkOutlined />,
            label: "Apri risultati AI dell'organizzazione",
            onClick: () => window.open(
              getOrgDetailUrl(attachment.org_pointer.objectId, activeTypeSlugs),
              "_blank",
              "noopener,noreferrer"
            ),
          },
        ]
      : []),
  ];

  const renderPreview = () => {
    if (showExtraLayer && previews.extraLayer) {
      return (
        <img
          src={previews.extraLayer}
          alt={extraLayerLabel.toLowerCase()}
        />
      );
    }

    if (previews.before && previews.after) {
      return (
        <ReactCompareSlider
          changePositionOnHover
          itemOne={(
            <ReactCompareSliderImage
              src={previews.after}
              alt="Risultato"
              style={{ objectFit: "contain" }}
            />
          )}
          itemTwo={(
            <ReactCompareSliderImage
              src={previews.before}
              alt="Originale"
              style={{ objectFit: "contain" }}
            />
          )}
          style={{ height: "100%", width: "100%" }}
        />
      );
    }

    if (previews.before || previews.after) {
      const availablePreview = previews.after || previews.before;
      return (
        <>
          <img src={availablePreview} alt="Anteprima disponibile" />
          <Tag color="warning" style={{ bottom: 8, position: "absolute" }}>
            {previews.after
              ? "Immagine originale non disponibile"
              : "Risultato non disponibile"}
          </Tag>
        </>
      );
    }

    return <Text type="secondary">Immagini non disponibili</Text>;
  };

  return (
    <Card
      className="giotto-ai-results__card"
      title={(
        <Space size={[4, 4]} wrap>
          {types.map((type) => (
            <Tag color="geekblue" key={type.slug}>{type.label}</Tag>
          ))}
        </Space>
      )}
      extra={(
        <Space>
          {extraLayerUrl && (
            <Space size={4}>
              <Text style={{ fontSize: 12 }}>{extraLayerLabel}</Text>
              <Switch
                checked={showExtraLayer}
                onChange={setShowExtraLayer}
                size="small"
              />
            </Space>
          )}
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button aria-label="Azioni risultato AI" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      )}
    >
      <div className="giotto-ai-results__preview">
        {renderPreview()}
      </div>

      <div className="giotto-ai-results__meta">
        <Tag style={{ width: "fit-content" }}>
          {formatDistance(new Date(attachment.createdAt), new Date(), { locale: it })}
        </Tag>
        {showOrganization && attachment.org_pointer?.nome && (
          <Tag
            color="blue"
            style={{
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "fit-content",
            }}
            title={attachment.org_pointer.nome}
          >
            {attachment.org_pointer.nome}
          </Tag>
        )}
        {attachment.aiEditPrompt && (
          <div className="giotto-ai-results__prompt">
            <strong>Prompt:</strong> {attachment.aiEditPrompt}
          </div>
        )}
      </div>
    </Card>
  );
};

const GiottoAiResults = ({ embedded = false, mode, orgId = null }) => {
  const history = useHistory();
  const location = useLocation();
  const { isProd } = useAppContext();
  const isGlobalMode = mode === "global";
  const isOrgMode = mode === "org";
  const validMode = isGlobalMode || isOrgMode;
  const validOrgScope = !isOrgMode || Boolean(orgId);
  const params = useMemo(
    () => queryString.parse(location.search),
    [location.search]
  );
  const { typeSlugs, invalidTypeSlugs } = useMemo(
    () => parseAiTypesParam(params.types),
    [params.types]
  );
  const selectedOrgId = isGlobalMode ? getScalarParam(params.org) : null;
  const hasValidFilters = invalidTypeSlugs.length === 0;
  const enabled = validMode && validOrgScope && hasValidFilters;
  const scopedOrgId = isOrgMode ? orgId : selectedOrgId;
  const {
    items,
    count,
    hasMore,
    loading,
    loadingMore,
    initialFetchDone,
    isCurrentQuery,
    error,
    loadMore,
    refresh,
  } = useGiottoAiResults({
    typeSlugs,
    orgId: scopedOrgId,
    enabled,
  });

  const replaceParams = useCallback((changes) => {
    const currentParams = queryString.parse(location.search);
    const nextParams = { ...currentParams, ...changes };
    Object.keys(nextParams).forEach((key) => {
      if (
        nextParams[key] === undefined ||
        nextParams[key] === null ||
        nextParams[key] === ""
      ) {
        delete nextParams[key];
      }
    });
    const search = queryString.stringify(nextParams);
    history.replace(`${location.pathname}${search ? `?${search}` : ""}`);
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    if (isOrgMode && params.org !== undefined) {
      replaceParams({ org: undefined });
    }
  }, [isOrgMode, params.org, replaceParams]);

  const handleTypeChange = (nextTypeSlugs) => {
    replaceParams({ types: serializeAiTypesParam(nextTypeSlugs) });
  };

  const handleOrganizationChange = (nextOrgId) => {
    replaceParams({ org: nextOrgId || undefined });
  };

  if (!validMode) {
    return (
      <Result
        status="error"
        title="Modalità risultati AI non valida"
      />
    );
  }

  if (!validOrgScope) {
    return (
      <Result
        status="error"
        title="Organizzazione non disponibile"
        subTitle="La vista organizzazione richiede un orgId valido."
      />
    );
  }

  const title = isGlobalMode ? "Giotto - Risultati AI" : "Risultati AI";

  return (
    <div
      className={`giotto-ai-results${isOrgMode ? " giotto-ai-results--org" : ""}${embedded ? " giotto-ai-results--embedded" : ""}`}
    >
      {isGlobalMode && !embedded && (
        <>
          <Breadcrumb style={{ marginBottom: 20 }}>
            <Breadcrumb.Item>Giotto</Breadcrumb.Item>
            <Breadcrumb.Item>Risultati AI</Breadcrumb.Item>
          </Breadcrumb>
          <Divider />
        </>
      )}

      <Space align="center" style={{ marginBottom: 24 }} wrap>
        <Title level={embedded ? 4 : (isGlobalMode ? 3 : 4)} style={{ margin: 0 }}>
          {title}
        </Title>
        <Badge overflowCount={5000} count={count} showZero />
      </Space>

      <div className="giotto-ai-results__filters">
        <div className="giotto-ai-results__filter">
          <Text strong>Tipologia AI</Text>
          <Select
            allowClear
            maxTagCount="responsive"
            mode="multiple"
            onChange={handleTypeChange}
            options={AI_RESULT_TYPES.map((type) => ({
              label: type.label,
              value: type.slug,
            }))}
            placeholder="Tutte le tipologie"
            value={typeSlugs}
          />
        </div>

        {isGlobalMode && (
          <div className="giotto-ai-results__filter giotto-ai-results__filter--org">
            <Text strong>Organizzazione</Text>
            <SelectOrg
              allowClear
              onChange={handleOrganizationChange}
              placeholder="Tutte le organizzazioni"
              style={{ width: "100%" }}
              value={selectedOrgId || undefined}
            />
          </div>
        )}

        <Button icon={<ReloadOutlined />} onClick={refresh} disabled={!enabled}>
          Aggiorna
        </Button>
      </div>

      {!hasValidFilters ? (
        <Result
          status="warning"
          title="Filtro tipologia non valido"
          subTitle={`Valori non riconosciuti: ${invalidTypeSlugs.join(", ")}`}
          extra={(
            <Button type="primary" onClick={() => replaceParams({ types: undefined })}>
              Mostra tutte le tipologie
            </Button>
          )}
        />
      ) : error ? (
        <Result
          status="error"
          title="Impossibile caricare i risultati AI"
          subTitle="Riprova tra qualche istante."
          extra={<Button onClick={refresh}>Riprova</Button>}
        />
      ) : !isCurrentQuery || (loading && !initialFetchDone) ? (
        <div style={{ padding: 48, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      ) : initialFetchDone && items.length === 0 ? (
        <Result
          status="info"
          title="Nessun risultato AI trovato"
          subTitle={isOrgMode
            ? "Non ci sono risultati per questa organizzazione e i filtri selezionati."
            : "Non ci sono risultati per i filtri selezionati."}
        />
      ) : (
        <InfiniteScroll
          dataLength={items.length}
          endMessage={items.length > 0 ? (
            <div className="giotto-ai-results__end-message">
              Non ci sono altri risultati da caricare
            </div>
          ) : null}
          hasMore={hasMore}
          loader={loadingMore ? (
            <div style={{ padding: 20, textAlign: "center" }}>
              <Spin />
            </div>
          ) : null}
          next={loadMore}
        >
          <div className="giotto-ai-results__grid">
            {items.map((attachment) => (
              <GiottoAiResultCard
                activeTypeSlugs={typeSlugs}
                attachment={attachment}
                isProd={isProd}
                key={attachment.objectId}
                showOrganization={isGlobalMode}
              />
            ))}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
};

export default GiottoAiResults;
